// Typed fetch() wrapper, one function per backend endpoint. Centralises
// error handling so a failed request surfaces a clear message rather than an
// unhandled promise rejection deep inside a click handler.
import type { ClientRef, CurrentUser, PresentationState } from "./state";
import type { BriefImportPreview, ProjectSummary, TermsAcceptanceList, TermsDoc, TermsStatus } from "./types";
import type {
  AddressLabel,
  AdminUser,
  AreaPackageDefaults,
  AreaPackagePreviewRequest,
  AreaPackagePreviewResult,
  AuditLogEntry,
  BriefForm,
  ColumnInfo,
  ColumnValuesResponse,
  CommittedPackageRecord,
  ComplexityCacheStatus,
  CommittedPackagesSummary,
  ConceptDesign,
  ConfigNode,
  CostEstimateRecord,
  CostReport,
  CustomPackageRecord,
  DataCompletenessResponse,
  DatasetInfo,
  DetailedDesign,
  ForwardWorksOverlapResponse,
  GeometryResponse,
  InvoiceRecord,
  Group,
  GroupMember,
  ModuleId,
  ModuleInfo,
  NearbyCandidatesResponse,
  NearbyResponse,
  NetworkStateResponse,
  PackageBrowseRow,
  PackageReadiness,
  PackageReadinessResponse,
  ProgrammeReadinessResponse,
  PackageCostEstimate,
  CostMethodComparison,
  PackageFaultFinding,
  PackageNarrative,
  PackageDetail,
  PackagePresetRecord,
  PackageServiceGoals,
  PendingShare,
  PipeNarrative,
  PipesResponse,
  PmTask,
  PresenceViewer,
  ProjectDesignReference,
  ProjectGeometry,
  ProjectPhase,
  ProjectRecord,
  ReferenceLayerCatalogEntry,
  ReferenceLayerGeoJSON,
  RenewalImpactResponse,
  RunSnapshot,
  RunStartResponse,
  StalenessResponse,
  SummaryResponse,
  SupplierProfile,
  SupplierRecord,
  TenderRecord,
  TopPackagesByGoalResponse,
  VariationRecord,
  WssBackfillLocationsResult,
  WssItem,
  WssLocateResult,
  WssUploadResult,
} from "./types";

export class ApiError extends Error {
  constructor(
    public status: number,
    public url: string,
    message: string,
    // FastAPI's HTTPException(status, detail) response body is
    // {"detail": ...} — detail is the raw parsed value (string or object,
    // e.g. committed_packages' overlap-conflict payload) when the body was
    // JSON, undefined otherwise. Callers that need structured error info
    // (see panel.ts's commit-conflict handling) read this instead of
    // re-parsing `message`, which is just a display string.
    public detail?: unknown,
  ) {
    super(`${status} ${url}: ${message}`);
  }
}

/** A 404 — for an organisation without the full data grants the backend
 * returns 404 (not 403) from every route that reads pipeline data it may
 * not see (P7a data-access enforcement). A subclass of ApiError, so every
 * existing `instanceof ApiError` / `status === 404` check behaves exactly
 * as before; callers that want to show "not available in your data
 * profile" instead of a generic failure catch this type (dataProfile.ts's
 * loadErrorText). */
export class NotAvailableError extends ApiError {}

// Set by terms.ts: called when any request answers 403 terms_not_accepted
// (the terms version was bumped while the app was open), so the blocking
// accept dialog comes back instead of a string of failed requests.
let termsRequiredHandler: (() => void) | null = null;
export function setTermsRequiredHandler(fn: () => void): void {
  termsRequiredHandler = fn;
}

/** The error to throw for a failed response: NotAvailableError for a 404,
 * else a plain ApiError. */
function apiError(status: number, url: string, message: string, detail?: unknown): ApiError {
  if (status === 403 && detail === "terms_not_accepted") termsRequiredHandler?.();
  return status === 404 ? new NotAvailableError(status, url, message, detail) : new ApiError(status, url, message, detail);
}

async function errorDetail(res: Response): Promise<{ message: string; detail?: unknown }> {
  const responseBody = await res.text().catch(() => "");
  try {
    const parsed = JSON.parse(responseBody);
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      const detail = (parsed as { detail: unknown }).detail;
      return { message: typeof detail === "string" ? detail : JSON.stringify(detail), detail };
    }
  } catch {
    // Not JSON — fall through to the raw text.
  }
  return { message: responseBody || res.statusText };
}

// Guards handleUnauthorized against turning a *persistent* 401 (session
// cookie genuinely not being sent/accepted — confirmed as a real mobile
// browser failure mode, not just a theoretical one: a mobile report of "the
// page reloads constantly" traced back to this reloading unconditionally on
// every 401 with no backoff, so a 401 that recurs immediately after reload
// — same cookie, same problem — reloaded forever) into an infinite reload
// loop. sessionStorage (not a module variable) survives the reload itself,
// which is the whole point.
const UNAUTHORIZED_RELOAD_GUARD_KEY = "wcc_401_reload_guard";
const UNAUTHORIZED_RELOAD_GUARD_WINDOW_MS = 5000;

function showSessionLostBanner(): void {
  if (document.getElementById("session-lost-banner")) return; // already showing
  const banner = document.createElement("div");
  banner.id = "session-lost-banner";
  banner.textContent = "Lost the session and couldn't recover automatically — tap to reload.";
  banner.style.position = "fixed";
  banner.style.top = "0";
  banner.style.left = "0";
  banner.style.right = "0";
  banner.style.zIndex = "3000"; // above toast.ts's 2000
  banner.style.background = "#b3261e";
  banner.style.color = "#fff";
  banner.style.padding = "calc(0.6rem + env(safe-area-inset-top, 0px)) 1rem 0.6rem"; // clear the iPhone island (row #41)
  banner.style.textAlign = "center";
  banner.style.fontSize = "0.85rem";
  banner.style.cursor = "pointer";
  banner.addEventListener("click", () => {
    sessionStorage.removeItem(UNAUTHORIZED_RELOAD_GUARD_KEY);
    window.location.reload();
  });
  document.body.prepend(banner);
}

/** A 401 from anything other than the auth endpoints themselves means the
 * session expired (or was revoked) while this tab was open — reload so
 * main.ts's bootstrap re-checks auth and shows the login screen fresh,
 * rather than every call site needing to handle "logged out mid-session"
 * individually. The auth endpoints' own 401s (a failed login attempt, or
 * the startup "am I logged in" check before any session exists) are
 * legitimate expected responses, not staleness — those must bubble up as a
 * normal ApiError instead, or login would infinite-reload-loop before ever
 * showing the form.
 *
 * At most one automatic reload per UNAUTHORIZED_RELOAD_GUARD_WINDOW_MS — if
 * a 401 recurs that fast, reloading again would just repeat whatever broke
 * the first time, so this stops and asks the user to reload manually instead
 * of hammering the browser. */
function handleUnauthorized(status: number, url: string): void {
  if (status !== 401 || url.startsWith("/api/auth/")) return;
  const now = Date.now();
  const last = Number(sessionStorage.getItem(UNAUTHORIZED_RELOAD_GUARD_KEY) ?? "0");
  if (now - last < UNAUTHORIZED_RELOAD_GUARD_WINDOW_MS) {
    showSessionLostBanner();
    return;
  }
  sessionStorage.setItem(UNAUTHORIZED_RELOAD_GUARD_KEY, String(now));
  window.location.reload();
}

/** Fired on `document` after every successful mutating request made through
 * this module (detail: {method, url}). Screens that show state other screens
 * change (approvalsInbox.ts's count) listen for it instead of each call site
 * having to tell them. */
export const API_MUTATED_EVENT = "wcc-api-mutated";
export interface ApiMutatedDetail {
  method: string;
  url: string;
}

/** Announce a successful mutation (exported for the few call sites that
 * need a raw fetch, e.g. pmAssists' file-or-JSON POST). */
export function noteMutation(method: string | undefined, url: string): void {
  const m = (method ?? "GET").toUpperCase();
  if (m === "GET" || m === "HEAD" || m === "OPTIONS") return;
  try {
    document.dispatchEvent(new CustomEvent<ApiMutatedDetail>(API_MUTATED_EVENT, { detail: { method: m, url } }));
  } catch {
    // a listener's failure must never fail the request that succeeded
  }
}

export async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    handleUnauthorized(res.status, url);
    const { message, detail } = await errorDetail(res);
    throw apiError(res.status, url, message, detail);
  }
  noteMutation(init?.method, url);
  return (await res.json()) as T;
}

export async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    handleUnauthorized(res.status, url);
    const { message, detail } = await errorDetail(res);
    throw apiError(res.status, url, message, detail);
  }
  noteMutation("POST", url);
  return (await res.json()) as T;
}

export async function patchJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    handleUnauthorized(res.status, url);
    const { message, detail } = await errorDetail(res);
    throw apiError(res.status, url, message, detail);
  }
  noteMutation("PATCH", url);
  return (await res.json()) as T;
}

export async function deleteJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { method: "DELETE" });
  if (!res.ok) {
    handleUnauthorized(res.status, url);
    const { message, detail } = await errorDetail(res);
    throw apiError(res.status, url, message, detail);
  }
  noteMutation("DELETE", url);
  return (await res.json()) as T;
}

/** multipart/form-data POST — no explicit Content-Type header, so the
 * browser sets it (with the correct boundary) itself; setting it manually
 * on a FormData body is a classic footgun that breaks the boundary. */
export async function postForm<T>(url: string, form: FormData): Promise<T> {
  const res = await fetch(url, { method: "POST", body: form });
  if (!res.ok) {
    handleUnauthorized(res.status, url);
    const { message, detail } = await errorDetail(res);
    if (res.status === 413) throw uploadTooLargeError(url, detail);
    throw apiError(res.status, url, message, detail);
  }
  noteMutation("POST", url);
  return (await res.json()) as T;
}

/** A 413 from an upload endpoint (the server's upload_limits.py) carries a
 * plain sentence naming the limit — surface exactly that as the error's
 * message (no "413 /api/...:" prefix), so every caller that shows
 * `err.message` shows something a user can act on. A 413 with no JSON
 * detail (e.g. from a proxy in front of the app) gets a generic sentence. */
function uploadTooLargeError(url: string, detail: unknown): ApiError {
  const text = typeof detail === "string" && detail ? detail : "Upload is too large for the server to accept.";
  const err = new ApiError(413, url, text, text);
  err.message = text;
  return err;
}

/** The message to show for a failed upload: the server's size-limit
 * sentence for a 413, else `fallback`. */
export function uploadErrorText(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.status === 413) return e.message;
  return fallback;
}

// --- Auth ---

export function getCurrentUser(): Promise<CurrentUser> {
  return getJson("/api/auth/me");
}

export function login(username: string, password: string, totpCode?: string): Promise<CurrentUser> {
  return postJson("/api/auth/login", totpCode ? { username, password, totp_code: totpCode } : { username, password });
}

// --- Presentation / demo mode (routers/auth.py, presentation.py) ---
export function setPresentationMode(on: boolean, maskStaff = false): Promise<PresentationState> {
  return postJson("/api/auth/presentation", { on, mask_staff: maskStaff });
}

export interface PresentationDictionary {
  suppliers: { label: string; names: string[] }[];
  staff: { label: string; names: string[] }[];
  /** tender / rate-set / tender-document file names ("Tender file A.pdf");
   * optional so an older server's answer still works */
  files?: { label: string; names: string[] }[];
}

export function getPresentationDictionary(): Promise<PresentationDictionary> {
  return getJson("/api/auth/presentation/dictionary");
}

export function logout(): Promise<{ status: string }> {
  return postJson("/api/auth/logout", {});
}

// --- View as organisation (routers/acting_client.py; platform admins) ---

export type ActingClientInfo = ClientRef & { allow_edits: boolean; since: string | null };

export interface OrgChoice extends ClientRef {
  user_count: number;
  profile_kind: "full" | "partial" | "public" | null;
  profile_label: string | null;
  gis_only: boolean | null;
}

export interface ActingClientState {
  home_client: ClientRef | null;
  acting_client: ActingClientInfo | null;
}

export function getActingClient(): Promise<ActingClientState & { clients: OrgChoice[] }> {
  return getJson("/api/admin/acting-client");
}

/** clientId null (or the home organisation) = return home. */
export function setActingClient(clientId: number | null, allowEdits = false): Promise<ActingClientState> {
  return postJson("/api/admin/acting-client", { client_id: clientId, allow_edits: allowEdits });
}

// --- Terms of use (routers/terms.py) ---

export function getTerms(): Promise<TermsDoc> {
  return getJson("/api/terms");
}

export function acceptTerms(version: string): Promise<TermsStatus> {
  return postJson("/api/terms/accept", { version });
}

export function acknowledgeTerms(): Promise<TermsStatus> {
  return postJson("/api/terms/acknowledge", {});
}

export function getTermsAcceptances(): Promise<TermsAcceptanceList> {
  return getJson("/api/admin/terms-acceptances");
}

export function getDatasets(): Promise<DatasetInfo[]> {
  return getJson("/api/datasets");
}

export function getColumns(dataset: string, waterTypes: string[]): Promise<ColumnInfo[]> {
  const q = new URLSearchParams({ water_types: waterTypes.join(",") });
  return getJson(`/api/datasets/${encodeURIComponent(dataset)}/columns?${q}`);
}

export function getSummary(
  dataset: string,
  column: string,
  waterTypes: string[],
): Promise<SummaryResponse> {
  const q = new URLSearchParams({ column, water_types: waterTypes.join(",") });
  return getJson(`/api/datasets/${encodeURIComponent(dataset)}/summary?${q}`);
}

export function getColumnValues(
  dataset: string,
  column: string,
  waterTypes: string[],
): Promise<ColumnValuesResponse> {
  const q = new URLSearchParams({ column, water_types: waterTypes.join(",") });
  return getJson(`/api/datasets/${encodeURIComponent(dataset)}/column-values?${q}`);
}

export function getGeometry(): Promise<GeometryResponse> {
  return getJson("/api/geometry");
}

export function getPipes(
  dataset: string,
  colorCol: string,
  waterTypes: string[],
): Promise<PipesResponse> {
  const q = new URLSearchParams({
    dataset,
    color_col: colorCol,
    water_types: waterTypes.join(","),
  });
  return getJson(`/api/pipes?${q}`);
}

export function getAddressLabels(
  west: number, south: number, east: number, north: number,
): Promise<AddressLabel[]> {
  const q = new URLSearchParams({
    west: String(west), south: String(south), east: String(east), north: String(north),
  });
  return getJson(`/api/addresses?${q}`);
}

export function getPackageBrowse(dataset: string): Promise<PackageBrowseRow[]> {
  // include=readiness: every row also carries its shovel-readiness label + %
  return getJson(`/api/packages/browse?${new URLSearchParams({ dataset, include: "readiness" })}`);
}

export function getPackageReadiness(dataset: string): Promise<PackageReadinessResponse> {
  return getJson(`/api/packages/readiness?${new URLSearchParams({ dataset })}`);
}

export function getOnePackageReadiness(packageId: string, dataset: string): Promise<PackageReadiness> {
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/readiness?${new URLSearchParams({ dataset })}`);
}

export function getProgrammeReadiness(dataset: string): Promise<ProgrammeReadinessResponse> {
  return getJson(`/api/dashboard/programme-readiness?${new URLSearchParams({ dataset })}`);
}

export function getPackageDetail(packageId: string, dataset: string): Promise<PackageDetail> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}?${q}`);
}

export function packageExportUrl(packageId: string, dataset: string): string {
  const q = new URLSearchParams({ dataset });
  return `/api/packages/${encodeURIComponent(packageId)}/export?${q}`;
}

export function packageActivityBriefUrl(packageId: string, dataset: string): string {
  const q = new URLSearchParams({ dataset });
  return `/api/packages/${encodeURIComponent(packageId)}/activity-brief?${q}`;
}

export function getPackageNearby(packageId: string, dataset: string): Promise<NearbyResponse> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/nearby?${q}`);
}

export function getNearbyCandidatePipes(packageId: string, dataset: string): Promise<NearbyCandidatesResponse> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/nearby-candidates?${q}`);
}

export function getPackageNarrative(packageId: string, dataset: string): Promise<PackageNarrative> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/narrative?${q}`);
}

export function getPipeNarrative(assetId: string): Promise<PipeNarrative> {
  return getJson(`/api/pipes/${encodeURIComponent(assetId)}/narrative`);
}

export function getPackageServiceGoals(packageId: string, dataset: string): Promise<PackageServiceGoals> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/service-goals?${q}`);
}

/** Slow — recalculates the real cost-estimate workbook via Excel COM
 * automation server-side (a few seconds), so callers should show a loading
 * state rather than blocking on it before rendering the rest of the panel. */
export function getPackageCostEstimate(packageId: string, dataset: string): Promise<PackageCostEstimate> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/cost-estimate?${q}`);
}

export function getPackageCostMethods(packageId: string, dataset: string): Promise<CostMethodComparison> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/cost-methods?${q}`);
}

export function getPackageFaultFinding(packageId: string, dataset: string): Promise<PackageFaultFinding> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/fault-finding?${q}`);
}

export function packageFaultFindingMapUrl(packageId: string, dataset: string): string {
  const q = new URLSearchParams({ dataset });
  return `/api/packages/${encodeURIComponent(packageId)}/fault-finding-map?${q}`;
}

export function packageLocationMapUrl(packageId: string, dataset: string): string {
  const q = new URLSearchParams({ dataset });
  return `/api/packages/${encodeURIComponent(packageId)}/location-map?${q}`;
}

export function packageAssetPlanningScopeUrl(packageId: string, dataset: string): string {
  const q = new URLSearchParams({ dataset });
  return `/api/packages/${encodeURIComponent(packageId)}/asset-planning-scope?${q}`;
}

export function getConceptDesign(packageId: string, dataset: string): Promise<ConceptDesign> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/concept-design?${q}`);
}

export function conceptDesignSvgUrl(packageId: string, dataset: string): string {
  const q = new URLSearchParams({ dataset });
  return `/api/packages/${encodeURIComponent(packageId)}/concept-design.svg?${q}`;
}

export function getDetailedDesign(packageId: string, dataset: string): Promise<DetailedDesign> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/detailed-design?${q}`);
}

export function detailedDesignLineSvgUrl(packageId: string, dataset: string, sheetId: string): string {
  const q = new URLSearchParams({ dataset });
  return `/api/packages/${encodeURIComponent(packageId)}/detailed-design/${encodeURIComponent(sheetId)}.svg?${q}`;
}

export function detailedDesignPdfUrl(packageId: string, dataset: string): string {
  const q = new URLSearchParams({ dataset });
  return `/api/packages/${encodeURIComponent(packageId)}/detailed-design.pdf?${q}`;
}

export function getBriefForm(packageId: string, dataset: string): Promise<BriefForm> {
  const q = new URLSearchParams({ dataset });
  return getJson(`/api/packages/${encodeURIComponent(packageId)}/brief-form?${q}`);
}

export function updateBriefForm(
  packageId: string,
  dataset: string,
  overrides: Record<string, string>,
): Promise<BriefForm> {
  const q = new URLSearchParams({ dataset });
  return patchJson(`/api/packages/${encodeURIComponent(packageId)}/brief-form?${q}`, { overrides });
}

/** "Reset to defaults" — discards every saved override for this package. */
export function resetBriefForm(packageId: string, dataset: string): Promise<BriefForm> {
  const q = new URLSearchParams({ dataset });
  return deleteJson(`/api/packages/${encodeURIComponent(packageId)}/brief-form?${q}`);
}

/** Best-guess suggestions ({field_key: text}) — a "what would I suggest"
 * query only, never saves anything. "confident": pipeline-derived fields
 * only. "confident_and_guess": also fills every other textarea via the
 * phrase library's top-ranked match for this package. */
export function autoPopulateBriefForm(
  packageId: string,
  dataset: string,
  mode: "confident" | "confident_and_guess",
): Promise<Record<string, string>> {
  const q = new URLSearchParams({ dataset });
  return postJson(`/api/packages/${encodeURIComponent(packageId)}/brief-form/auto-populate?${q}`, { mode });
}

/** Recommended-package boundary hulls — a plain GeoJSON FeatureCollection of
 * Polygons, each with a package_id property. Typed loosely (no @types/geojson
 * dependency) since map.ts only needs .features[].geometry/.properties. */
export interface HullFeatureCollection {
  type: "FeatureCollection";
  features: { type: "Feature"; properties: { package_id: string }; geometry: unknown }[];
}

export function getPackageHulls(dataset: string): Promise<HullFeatureCollection> {
  return getJson(`/api/packages/hulls?${new URLSearchParams({ dataset })}`);
}

export function getReferenceLayerCatalog(): Promise<ReferenceLayerCatalogEntry[]> {
  return getJson("/api/reference-layers");
}

export function getReferenceLayer(name: string): Promise<ReferenceLayerGeoJSON> {
  return getJson(`/api/reference-layers/${encodeURIComponent(name)}`);
}

export interface GroundwaterDepthRasterResponse {
  image: string;
  bounds: [number, number, number, number];
}

export function getGroundwaterDepthRaster(): Promise<GroundwaterDepthRasterResponse> {
  return getJson("/api/reference-layers/raster/groundwater-depth");
}

export function getStaleness(datasetName: string): Promise<StalenessResponse> {
  return getJson(`/api/datasets/${encodeURIComponent(datasetName)}/staleness`);
}

export function getModules(): Promise<Record<ModuleId, ModuleInfo>> {
  return getJson("/api/modules");
}

export function getModuleConfig(moduleId: ModuleId): Promise<Record<string, ConfigNode>> {
  return getJson(`/api/modules/${encodeURIComponent(moduleId)}/config`);
}

export function saveModuleConfig(
  moduleId: ModuleId,
  configs: Record<string, ConfigNode>,
): Promise<{ status: string; configs: string[] }> {
  return postJson(`/api/modules/${encodeURIComponent(moduleId)}/config`, { configs });
}

export function runModule(moduleId: ModuleId, mode: "fast" | "full"): Promise<RunStartResponse> {
  return postJson(`/api/modules/${encodeURIComponent(moduleId)}/run`, { mode });
}

export function getComplexityCacheStatus(): Promise<ComplexityCacheStatus> {
  return getJson("/api/modules/complexity-cache/status");
}

export function getRunSnapshot(runId: string): Promise<RunSnapshot> {
  return getJson(`/api/modules/runs/${encodeURIComponent(runId)}`);
}

/** Live log stream for a run, via the browser's native EventSource (SSE).
 * onLine is called for every output line as it arrives; onDone is called
 * once with the final {status, returncode} when the run finishes, after
 * which the EventSource is closed automatically. */
export function streamRun(
  runId: string,
  onLine: (line: string) => void,
  onDone: (final: { status: string; returncode: number | null }) => void,
): () => void {
  const source = new EventSource(`/api/modules/runs/${encodeURIComponent(runId)}/stream`);
  source.onmessage = (event) => onLine(JSON.parse(event.data));
  source.addEventListener("done", (event) => {
    onDone(JSON.parse((event as MessageEvent).data));
    source.close();
  });
  source.onerror = () => {
    // EventSource retries automatically on transient errors; if the server
    // already sent "done" we've closed it ourselves above, so an error here
    // only fires for a genuine connection problem — surface it as done/error
    // rather than leaving the caller's log pane hanging forever.
    if (source.readyState === EventSource.CLOSED) {
      onDone({ status: "error", returncode: null });
    }
  };
  return () => source.close();
}

/** Promise-wrapped runModule+streamRun, for callers that just want to await
 * a run's completion (e.g. running several modules in sequence) rather than
 * managing the EventSource callbacks directly. */
export function runModuleAndWait(
  moduleId: ModuleId,
  mode: "fast" | "full",
  onLine: (line: string) => void,
): Promise<{ status: string; returncode: number | null; script: string }> {
  return runModule(moduleId, mode).then(
    (start) =>
      new Promise((resolve) => {
        streamRun(start.run_id, onLine, (final) => resolve({ ...final, script: start.script }));
      }),
  );
}

// --- Custom (user-editable) packages ---

export function createCustomPackage(body: {
  name: string;
  water_type: string;
  asset_ids: string[];
  based_on_package_id: string | null;
  based_on_dataset: string | null;
  wizard_config?: Record<string, unknown> | null;
  brief_id?: string | null;
}): Promise<CustomPackageRecord> {
  return postJson("/api/custom-packages", body);
}

export function listCustomPackages(): Promise<CustomPackageRecord[]> {
  return getJson("/api/custom-packages");
}

export function updateCustomPackage(
  customId: string,
  body: { name?: string; asset_ids?: string[]; water_type?: string; wizard_config?: Record<string, unknown> | null },
): Promise<CustomPackageRecord> {
  return patchJson(`/api/custom-packages/${encodeURIComponent(customId)}`, body);
}

export function deleteCustomPackage(customId: string): Promise<{ status: string; id: string }> {
  return deleteJson(`/api/custom-packages/${encodeURIComponent(customId)}`);
}

export function splitCustomPackage(customId: string): Promise<{ status: string; packages: CustomPackageRecord[] }> {
  return postJson(`/api/custom-packages/${encodeURIComponent(customId)}/split`, {});
}

// --- "Create custom package from an area" wizard (areaPackageWizard.ts) ---

export function previewAreaPackage(body: AreaPackagePreviewRequest): Promise<AreaPackagePreviewResult> {
  return postJson("/api/area-packages/preview", body);
}

export function getAreaPackageDefaults(): Promise<AreaPackageDefaults> {
  return getJson("/api/area-packages/defaults");
}

export function listPackagePresets(): Promise<PackagePresetRecord[]> {
  return getJson("/api/package-presets");
}

export function createPackagePreset(body: Omit<PackagePresetRecord, "id" | "owner" | "created_at" | "updated_at">): Promise<PackagePresetRecord> {
  return postJson("/api/package-presets", body);
}

export function updatePackagePreset(
  presetId: string,
  body: Partial<Omit<PackagePresetRecord, "id" | "owner" | "created_at" | "updated_at">>,
): Promise<PackagePresetRecord> {
  return patchJson(`/api/package-presets/${encodeURIComponent(presetId)}`, body);
}

export function deletePackagePreset(presetId: string): Promise<{ status: string; id: string }> {
  return deleteJson(`/api/package-presets/${encodeURIComponent(presetId)}`);
}

// --- WSS viewer (NOE/FWP programme items -> packages) ---

export function listWssItems(): Promise<WssItem[]> {
  return getJson("/api/wss-items");
}

export function uploadWssItems(file: File): Promise<WssUploadResult> {
  const form = new FormData();
  form.append("file", file);
  return postForm("/api/wss-items/upload", form);
}

export function locateWssItem(itemId: string): Promise<WssLocateResult> {
  return postJson(`/api/wss-items/${encodeURIComponent(itemId)}/locate`, {});
}

export function backfillWssLocations(): Promise<WssBackfillLocationsResult> {
  return postJson("/api/wss-items/backfill-locations", {});
}

export function commitWssItem(itemId: string, customPackageId: string): Promise<WssItem> {
  return postJson(`/api/wss-items/${encodeURIComponent(itemId)}/commit`, { custom_package_id: customPackageId });
}

export function uncommitWssItem(itemId: string): Promise<WssItem> {
  return postJson(`/api/wss-items/${encodeURIComponent(itemId)}/uncommit`, {});
}

// --- Committed (locked-in, read-only) packages ---

export function commitPackageWorks(body: {
  name: string;
  water_type: string;
  asset_ids: string[];
  based_on_package_id: string | null;
  based_on_dataset: string | null;
}): Promise<CommittedPackageRecord> {
  return postJson("/api/committed-packages", body);
}

export function listCommittedPackages(): Promise<CommittedPackageRecord[]> {
  return getJson("/api/committed-packages");
}

/** Uncommits a package, moving it back into "custom packages" as an
 * editable draft (with any saved review-form overrides carried over) —
 * returns the new custom-package record. */
export function uncommitPackage(committedId: string): Promise<CustomPackageRecord> {
  return postJson(`/api/committed-packages/${encodeURIComponent(committedId)}/uncommit`, {});
}

export function deleteCommittedPackage(committedId: string): Promise<{ status: string; id: string }> {
  return deleteJson(`/api/committed-packages/${encodeURIComponent(committedId)}`);
}

// --- Presence indicator ---

/** Records that the current user is viewing (targetType, targetId) right
 * now, and returns everyone *else* currently viewing the same thing. */
export function pingPresence(targetType: string, targetId: string): Promise<PresenceViewer[]> {
  return postJson(`/api/presence/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}/ping`, {});
}

// --- Manager: own-group-only user list + create (routers/manager_users.py) ---

export function listGroupUsers(): Promise<AdminUser[]> {
  return getJson("/api/manager/users");
}

export function createGroupUser(body: {
  username: string;
  display_name: string;
  role: string;
  password: string;
  teams?: string[];
}): Promise<AdminUser> {
  return postJson("/api/manager/users", body);
}

// --- Manager Dashboard (wcc_viewer.dashboard / routers/dashboard.py) ---

export function getDashboardNetworkState(): Promise<NetworkStateResponse> {
  return getJson("/api/dashboard/network-state");
}

export function getDashboardCommittedPackagesSummary(): Promise<CommittedPackagesSummary> {
  return getJson("/api/dashboard/committed-packages-summary");
}

export function getDashboardRenewalImpact(): Promise<RenewalImpactResponse> {
  return getJson("/api/dashboard/renewal-impact");
}

export function getDashboardDataCompleteness(): Promise<DataCompletenessResponse> {
  return getJson("/api/dashboard/data-completeness");
}

export function getDashboardTopPackagesByGoal(): Promise<TopPackagesByGoalResponse> {
  return getJson("/api/dashboard/top-packages-by-goal");
}

export function getDashboardForwardWorksOverlap(): Promise<ForwardWorksOverlapResponse> {
  return getJson("/api/dashboard/forward-works-overlap");
}

// --- Admin: user management + activity log ---

export function listAdminUsers(): Promise<AdminUser[]> {
  return getJson("/api/admin/users");
}

export function createAdminUser(body: {
  username: string;
  display_name: string;
  role: string;
  password: string;
  teams?: string[];
  email?: string;
}): Promise<AdminUser> {
  return postJson("/api/admin/users", body);
}

export function updateAdminUser(
  userId: number,
  body: { display_name?: string; role?: string; active?: boolean; password?: string; teams?: string[]; email?: string },
): Promise<AdminUser> {
  return patchJson(`/api/admin/users/${userId}`, body);
}

export function getAuditLog(filters: { username?: string; action?: string; limit?: number } = {}): Promise<AuditLogEntry[]> {
  const q = new URLSearchParams();
  if (filters.username) q.set("username", filters.username);
  if (filters.action) q.set("action", filters.action);
  q.set("limit", String(filters.limit ?? 100));
  return getJson(`/api/audit-log?${q}`);
}

// --- Admin: groups ---

export function listAdminGroups(): Promise<Group[]> {
  return getJson("/api/admin/groups");
}

export function createAdminGroup(name: string): Promise<Group> {
  return postJson("/api/admin/groups", { name });
}

export function renameAdminGroup(groupId: number, name: string): Promise<Group> {
  return patchJson(`/api/admin/groups/${groupId}`, { name });
}

export function deleteAdminGroup(groupId: number): Promise<{ status: string; id: number }> {
  return deleteJson(`/api/admin/groups/${groupId}`);
}

export function updateUserGroup(userId: number, groupId: number | null): Promise<AdminUser> {
  return patchJson(`/api/admin/users/${userId}/group`, { group_id: groupId });
}

// --- Package sharing ---

/** Other members of the current user's own group — the valid share-target
 * list. Empty if the user isn't assigned to a group. */
export function listGroupMembers(): Promise<GroupMember[]> {
  return getJson("/api/groups/members");
}

export function shareCustomPackage(customId: string, toUserId: number): Promise<{ status: string; to_username: string }> {
  return postJson(`/api/custom-packages/${encodeURIComponent(customId)}/share`, { to_user_id: toUserId });
}

/** One-shot poll: returns shares delivered to the current user since the
 * last poll, and marks them delivered server-side in the same call — a
 * given share is only ever returned once. */
export function pollPendingShares(): Promise<PendingShare[]> {
  return postJson("/api/shares/poll", {});
}

// --- Projects (PROJECT_INTERFACE_VISION.md Phase 2) ---

export function createProject(body: {
  name: string;
  source_type: "package" | "committed_package" | "external";
  asset_ids: string[];
  intention_text?: string | null;
  water_type?: string | null;
  source_package_id?: string | null;
  source_dataset?: string | null;
  /** review #32: one of GET /api/projects/frameworks; null/absent = the organisation's default */
  framework_key?: string | null;
  /** 2026-10-28: one of GET /api/projects/frameworks councils; null/absent = read from the pipes */
  council?: string | null;
}): Promise<ProjectRecord> {
  return postJson("/api/projects", body);
}

/** Archived (hidden) projects are left out unless asked for. */
export function listProjects(includeArchived = false): Promise<ProjectRecord[]> {
  return getJson(includeArchived ? "/api/projects?include_archived=true" : "/api/projects");
}

export function archiveProject(projectId: string, note: string | null = null): Promise<ProjectRecord> {
  return postJson(`/api/projects/${encodeURIComponent(projectId)}/archive`, { note });
}

export function unarchiveProject(projectId: string): Promise<ProjectRecord> {
  return postJson(`/api/projects/${encodeURIComponent(projectId)}/unarchive`, {});
}

/** Name/phase/RAG/pipes only — the strategy & packaging workspaces' view. */
export function listProjectSummaries(): Promise<ProjectSummary[]> {
  return getJson("/api/projects/summary");
}

export function getProject(projectId: string): Promise<ProjectRecord> {
  return getJson(`/api/projects/${encodeURIComponent(projectId)}`);
}

/** Just this project's own pipe paths — see routers/projects.py's own
 * docstring for why this is a dedicated, server-filtered endpoint rather
 * than reusing the full-network /api/geometry payload. */
export function getProjectGeometry(projectId: string): Promise<ProjectGeometry> {
  return getJson(`/api/projects/${encodeURIComponent(projectId)}/geometry`);
}

export function deleteProject(projectId: string): Promise<{ status: string; id: string }> {
  return deleteJson(`/api/projects/${encodeURIComponent(projectId)}`);
}

export function getProjectDesignReference(projectId: string): Promise<ProjectDesignReference> {
  return getJson(`/api/projects/${encodeURIComponent(projectId)}/design-reference`);
}

// --- Project lock-in + tasks (PROJECT_INTERFACE_VISION.md Phase 4) ---

export function lockProject(projectId: string): Promise<ProjectRecord> {
  return postJson(`/api/projects/${encodeURIComponent(projectId)}/lock`, {});
}

export function unlockProject(projectId: string): Promise<ProjectRecord> {
  return postJson(`/api/projects/${encodeURIComponent(projectId)}/unlock`, {});
}

export function listProjectTasks(projectId: string): Promise<PmTask[]> {
  return getJson(`/api/projects/${encodeURIComponent(projectId)}/tasks`);
}

export function createProjectTask(projectId: string, title: string, description?: string, phase?: string | null): Promise<PmTask> {
  return postJson(`/api/projects/${encodeURIComponent(projectId)}/tasks`, { title, description, phase: phase ?? null });
}

/** Re-file a task under a phase key (null = unphased). */
export function updateProjectTaskPhase(projectId: string, taskId: number, phase: string | null): Promise<PmTask> {
  return patchJson(`/api/projects/${encodeURIComponent(projectId)}/tasks/${taskId}`, { phase });
}

// --- Project delivery phases (wcc_viewer.projects.PHASES) ---

export function listProjectPhases(): Promise<ProjectPhase[]> {
  return getJson("/api/projects/meta/phases");
}

/** Move a project to any phase (configure+). 409 if a post-inception phase
 * is requested before the project is locked in. */
export function setProjectPhase(projectId: string, phase: string): Promise<ProjectRecord> {
  return postJson(`/api/projects/${encodeURIComponent(projectId)}/phase`, { phase });
}

/** Manual project health traffic light (configure+); health null = not assessed. */
export function setProjectHealth(projectId: string, health: ProjectRecord["health"], note: string | null): Promise<ProjectRecord> {
  return postJson(`/api/projects/${encodeURIComponent(projectId)}/health`, { health, note });
}

export function updateProjectTaskStatus(projectId: string, taskId: number, status: PmTask["status"]): Promise<PmTask> {
  return patchJson(`/api/projects/${encodeURIComponent(projectId)}/tasks/${taskId}`, { status });
}

export function deleteProjectTask(projectId: string, taskId: number): Promise<{ status: string; id: number }> {
  return deleteJson(`/api/projects/${encodeURIComponent(projectId)}/tasks/${taskId}`);
}

// --- Cost estimates + tenders (PROJECT_INTERFACE_VISION.md Phase 5) ---

/** Auto-generates a new versioned estimate from the project's own source
 * package when it has one; pass total/breakdown for a manual estimate
 * (the only option for an external project — see routers/projects.py). */
export function createCostEstimate(
  projectId: string,
  manual?: { total: number; breakdown: Record<string, unknown> },
  // Delivery framework: CEM level (omit = phase default) / Engineer's Estimate.
  opts?: { level?: number; estimate_type?: "level" | "engineers"; reason_for_change?: string },
): Promise<CostEstimateRecord> {
  return postJson(`/api/projects/${encodeURIComponent(projectId)}/cost-estimates`, { ...(manual ?? {}), ...(opts ?? {}) });
}

export function listCostEstimates(projectId: string): Promise<CostEstimateRecord[]> {
  return getJson(`/api/projects/${encodeURIComponent(projectId)}/cost-estimates`);
}

export function listSuppliers(): Promise<SupplierRecord[]> {
  return getJson("/api/suppliers");
}

export function createSupplier(name: string): Promise<SupplierRecord> {
  return postJson("/api/suppliers", { name });
}

export function listProjectTenders(projectId: string): Promise<TenderRecord[]> {
  return getJson(`/api/projects/${encodeURIComponent(projectId)}/tenders`);
}

export function createProjectTender(
  projectId: string,
  supplierId: number,
  amount: number,
  notes: string | null,
  document: File | null,
): Promise<TenderRecord> {
  const form = new FormData();
  form.append("supplier_id", String(supplierId));
  form.append("amount", String(amount));
  if (notes) form.append("notes", notes);
  if (document) form.append("document", document);
  return postForm(`/api/projects/${encodeURIComponent(projectId)}/tenders`, form);
}

export function updateTenderStatus(projectId: string, tenderId: number, status: TenderRecord["status"]): Promise<TenderRecord> {
  return patchJson(`/api/projects/${encodeURIComponent(projectId)}/tenders/${tenderId}`, { status });
}

export function deleteProjectTender(projectId: string, tenderId: number): Promise<{ status: string; id: number }> {
  return deleteJson(`/api/projects/${encodeURIComponent(projectId)}/tenders/${tenderId}`);
}

/** Not fetched via getJson — this hands back a real file, so the caller
 * just needs the URL to open/download it (same pattern as any other
 * attachment-style link in this app). */
export function tenderDocumentUrl(projectId: string, tenderId: number): string {
  return `/api/projects/${encodeURIComponent(projectId)}/tenders/${tenderId}/document`;
}

// --- Variations, invoices, cost report, supplier profile (PROJECT_INTERFACE_VISION.md Phase 6) ---

export function listProjectVariations(projectId: string): Promise<VariationRecord[]> {
  return getJson(`/api/projects/${encodeURIComponent(projectId)}/variations`);
}

export function createProjectVariation(
  projectId: string,
  supplierId: number,
  description: string,
  amount: number,
  tenderId: number | null,
): Promise<VariationRecord> {
  return postJson(`/api/projects/${encodeURIComponent(projectId)}/variations`, {
    supplier_id: supplierId, description, amount, tender_id: tenderId,
  });
}

export function updateVariationStatus(projectId: string, variationId: number, status: VariationRecord["status"]): Promise<VariationRecord> {
  return patchJson(`/api/projects/${encodeURIComponent(projectId)}/variations/${variationId}`, { status });
}

export function deleteProjectVariation(projectId: string, variationId: number): Promise<{ status: string; id: number }> {
  return deleteJson(`/api/projects/${encodeURIComponent(projectId)}/variations/${variationId}`);
}

export function listProjectInvoices(projectId: string): Promise<InvoiceRecord[]> {
  return getJson(`/api/projects/${encodeURIComponent(projectId)}/invoices`);
}

export function createProjectInvoice(
  projectId: string,
  amount: number,
  invoiceDate: string,
  supplierId: number | null,
  category: string | null,
  notes: string | null,
  document: File | null,
): Promise<InvoiceRecord> {
  const form = new FormData();
  form.append("amount", String(amount));
  form.append("invoice_date", invoiceDate);
  if (supplierId) form.append("supplier_id", String(supplierId));
  if (category) form.append("category", category);
  if (notes) form.append("notes", notes);
  if (document) form.append("document", document);
  return postForm(`/api/projects/${encodeURIComponent(projectId)}/invoices`, form);
}

export function deleteProjectInvoice(projectId: string, invoiceId: number): Promise<{ status: string; id: number }> {
  return deleteJson(`/api/projects/${encodeURIComponent(projectId)}/invoices/${invoiceId}`);
}

export function invoiceDocumentUrl(projectId: string, invoiceId: number): string {
  return `/api/projects/${encodeURIComponent(projectId)}/invoices/${invoiceId}/document`;
}

export function getProjectCostReport(projectId: string): Promise<CostReport> {
  return getJson(`/api/projects/${encodeURIComponent(projectId)}/cost-report`);
}

export function getSupplierProfile(supplierId: number): Promise<SupplierProfile> {
  return getJson(`/api/suppliers/${supplierId}/profile`);
}

// --- Import from Activity Brief (routers/brief_import.py) ---

export function previewBriefImport(file: File, council: string | null = null): Promise<BriefImportPreview> {
  const form = new FormData();
  form.append("file", file);
  if (council) form.append("council", council);
  return postForm("/api/brief-import/preview", form);
}

export function createPackageFromBrief(body: {
  token: string;
  name: string;
  asset_ids: string[];
  wss_item_id: string | null;
}): Promise<CustomPackageRecord> {
  return postJson("/api/brief-import/create", body);
}

export function briefImportSourceUrl(customId: string): string {
  return `/api/brief-import/${encodeURIComponent(customId)}/source`;
}

// --- Delivery framework (wcc_viewer.delivery_framework / routers/delivery.py) ---

const projUrl = (projectId: string) => `/api/projects/${encodeURIComponent(projectId)}`;

export function getProjectFramework(projectId: string): Promise<import("./types").ProjectFramework> {
  return getJson(`${projUrl(projectId)}/framework`);
}

export function updateDeliverySettings(
  projectId: string,
  fields: { delivery_approach?: string | null; complexity?: string | null; asset_operator?: string | null; project_type?: string | null },
): Promise<ProjectRecord> {
  return patchJson(`${projUrl(projectId)}/delivery-settings`, fields);
}

export function setPhaseApplicability(projectId: string, phaseKey: string, applicable: boolean, reason: string | null): Promise<{ phase_moved_to: string | null; project: ProjectRecord }> {
  return postJson(`${projUrl(projectId)}/phase-applicability`, { phase_key: phaseKey, applicable, reason });
}

export function updateDeliverable(projectId: string, deliverableId: number, status: string, reason?: string | null): Promise<import("./types").Deliverable> {
  return patchJson(`${projUrl(projectId)}/deliverables/${deliverableId}`, { status, reason: reason ?? null });
}

export function addDeliverableDocument(projectId: string, deliverableId: number, file: File | null, url: string | null, changeDescription: string | null): Promise<import("./types").ProjectDocument> {
  const form = new FormData();
  if (file) form.append("document", file);
  if (url) form.append("url", url);
  if (changeDescription) form.append("change_description", changeDescription);
  return postForm(`${projUrl(projectId)}/deliverables/${deliverableId}/documents`, form);
}

/** Builds a draft with the deliverable's generator and stores it as a new
 * version (404 until that generator exists). */
export function generateDeliverableDraft(projectId: string, deliverableId: number): Promise<import("./types").ProjectDocument> {
  return postJson(`${projUrl(projectId)}/deliverables/${deliverableId}/generate`, {});
}

export function projectDocumentUrl(projectId: string, documentId: number): string {
  return `${projUrl(projectId)}/project-documents/${documentId}/file`;
}

/** Same file served inline — only honoured for PDFs (embedded viewer). */
export function projectDocumentInlineUrl(projectId: string, documentId: number): string {
  return `${projUrl(projectId)}/project-documents/${documentId}/file?inline=1`;
}

/** Deliverable document pane: versions, HTML preview of one version and the
 * current version's fill-in parts (document_fill.view). */
export function getDeliverableDocumentView(
  projectId: string,
  deliverableId: number,
  opts: { versionId?: number | null; sheet?: string | null } = {},
): Promise<import("./types").DocumentView> {
  const q = new URLSearchParams();
  if (opts.versionId) q.set("version_id", String(opts.versionId));
  if (opts.sheet) q.set("sheet", opts.sheet);
  const qs = q.toString();
  return getJson(`${projUrl(projectId)}/deliverables/${deliverableId}/document-view${qs ? `?${qs}` : ""}`);
}

/** Writes answers into a new version ("" clears a filled part). Gateway
 * checklist cells are also written back to the gateway card (gateway_writeback). */
export function fillDeliverableDocument(
  projectId: string,
  deliverableId: number,
  answers: Record<string, string>,
): Promise<import("./types").DocumentView & { applied: string[]; reverted: string[]; gateway_writeback: import("./types").GatewayWriteback[] }> {
  return postJson(`${projUrl(projectId)}/deliverables/${deliverableId}/fill`, { answers });
}

/** "Generate all drafts" — background job, one per project (409 while running). */
export function startGenerateAll(
  projectId: string,
  opts: { phase_key?: string | null; include_uploaded?: boolean; include_approved?: boolean },
): Promise<import("./types").GenerateAllJob> {
  return postJson(`${projUrl(projectId)}/deliverables/generate-all`, opts);
}

export function getGenerateAllStatus(projectId: string): Promise<import("./types").GenerateAllJob> {
  return getJson(`${projUrl(projectId)}/deliverables/generate-all/status`);
}

/** Project outputs (Activity Brief export, cost workbook, indicative TMP). */
export function listProjectOutputs(projectId: string): Promise<import("./types").ProjectOutput[]> {
  return getJson(`${projUrl(projectId)}/outputs`);
}

export function getProjectOutputView(
  projectId: string,
  key: string,
  opts: { sheet?: string | null; refresh?: boolean } = {},
): Promise<import("./types").ProjectOutputView> {
  const q = new URLSearchParams();
  if (opts.sheet) q.set("sheet", opts.sheet);
  if (opts.refresh) q.set("refresh", "true");
  const qs = q.toString();
  return getJson(`${projUrl(projectId)}/outputs/${encodeURIComponent(key)}/view${qs ? `?${qs}` : ""}`);
}

/** "Page view": a deliverable version rendered to PDF server-side (Word/Excel or LibreOffice). */
export function deliverableDocumentPdfUrl(projectId: string, deliverableId: number, opts: { versionId?: number | null; sheet?: string | null } = {}): string {
  const q = new URLSearchParams();
  if (opts.versionId) q.set("version_id", String(opts.versionId));
  if (opts.sheet) q.set("sheet", opts.sheet);
  const qs = q.toString();
  return `${projUrl(projectId)}/deliverables/${deliverableId}/document-pdf${qs ? `?${qs}` : ""}`;
}

/** "Page view" of a project output (PDF rendering of the built file). */
export function projectOutputPdfUrl(projectId: string, key: string, sheet?: string | null): string {
  return `${projUrl(projectId)}/outputs/${encodeURIComponent(key)}/pdf${sheet ? `?sheet=${encodeURIComponent(sheet)}` : ""}`;
}

export function projectOutputFileUrl(projectId: string, key: string): string {
  return `${projUrl(projectId)}/outputs/${encodeURIComponent(key)}/file`;
}

/** Direct download of a freshly generated draft (delivery_docs router). */
export function deliveryDocDraftUrl(projectId: string, docKey: string, opts?: Record<string, string>): string {
  const q = opts && Object.keys(opts).length ? `?${new URLSearchParams(opts).toString()}` : "";
  return `${projUrl(projectId)}/documents/${encodeURIComponent(docKey)}/draft${q}`;
}

export function answerChecklistItem(
  projectId: string,
  gatewayKey: string,
  itemKey: string,
  fields: { answer?: string | null; evidence_url?: string | null; comment?: string | null },
): Promise<import("./types").ChecklistItem> {
  return patchJson(`${projUrl(projectId)}/gateways/${gatewayKey}/checklist/${encodeURIComponent(itemKey)}`, fields);
}

export function submitGateway(projectId: string, gatewayKey: string, body: { funding_envelope?: number | null; note?: string | null; acknowledge_unmet?: boolean }): Promise<import("./types").GatewayRecord> {
  return postJson(`${projUrl(projectId)}/gateways/${gatewayKey}/submit`, body);
}

export function decideGateway(projectId: string, gatewayKey: string, decision: string, note: string | null, conditions: string | null): Promise<{ gateway: import("./types").GatewayRecord; effects: Record<string, unknown>; project: ProjectRecord }> {
  return postJson(`${projUrl(projectId)}/gateways/${gatewayKey}/decision`, { decision, note, conditions });
}

export function listProjectRisks(projectId: string): Promise<import("./types").ProjectRisk[]> {
  return getJson(`${projUrl(projectId)}/risks`);
}

export function createProjectRisk(projectId: string, fields: Record<string, unknown>): Promise<import("./types").ProjectRisk> {
  return postJson(`${projUrl(projectId)}/risks`, fields);
}

export function updateProjectRisk(projectId: string, riskId: number, fields: Record<string, unknown>): Promise<import("./types").ProjectRisk> {
  return patchJson(`${projUrl(projectId)}/risks/${riskId}`, fields);
}

export function deleteProjectRisk(projectId: string, riskId: number): Promise<{ status: string }> {
  return deleteJson(`${projUrl(projectId)}/risks/${riskId}`);
}

export function getHealthHints(projectId: string): Promise<import("./types").HealthHints> {
  return getJson(`${projUrl(projectId)}/health-hints`);
}

/** RAG Calculator pillars ({pillar: {rag, note}}) — the overall is computed server-side. */
export function setProjectHealthPillars(projectId: string, pillars: Record<string, { rag: string | null; note?: string | null }>): Promise<ProjectRecord> {
  return postJson(`${projUrl(projectId)}/health`, { pillars });
}

// --- Flow plan stage A: notifications inbox, profile, project people/contacts ---

export function listNotifications(unreadOnly = false): Promise<{ items: import("./types").AppNotification[]; unread: number }> {
  return getJson(`/api/notifications?limit=30${unreadOnly ? "&unread_only=true" : ""}`);
}

export function getUnreadNotificationCount(): Promise<{ unread: number; latest_id?: number }> {
  return getJson("/api/notifications/unread-count");
}

/** Unread notifications newer than `sinceId` (newest first), for the bell's toasts. */
export function listNotificationsSince(sinceId: number): Promise<{ items: import("./types").AppNotification[]; unread: number }> {
  return getJson(`/api/notifications?limit=30&unread_only=true&since_id=${sinceId}`);
}

export function markNotificationRead(id: number): Promise<{ id: number; unread: number }> {
  return postJson(`/api/notifications/${id}/read`, {});
}

export function markAllNotificationsRead(): Promise<{ marked: number; unread: number }> {
  return postJson("/api/notifications/read-all", {});
}

/** Admin: turn off another user's two-factor sign-in (routers/admin.py). */
export function resetUserMfa(userId: number): Promise<unknown> {
  return postJson(`/api/admin/users/${userId}/mfa-reset`, {});
}

export function updateOwnProfile(body: { email?: string; display_name?: string }): Promise<CurrentUser> {
  return patchJson("/api/auth/me/profile", body);
}

export function getProjectContacts(projectId: string): Promise<import("./types").ProjectContact[]> {
  return getJson(`/api/projects/${encodeURIComponent(projectId)}/contacts`);
}

export function listProjectPeopleOptions(): Promise<import("./types").PersonOption[]> {
  return getJson("/api/projects/meta/people");
}

export function setProjectPeople(
  projectId: string,
  body: { pm_user_id?: number | null; owner_team?: string | null; sp_lead_user_id?: number | null },
): Promise<ProjectRecord> {
  return patchJson(`/api/projects/${encodeURIComponent(projectId)}/people`, body);
}

// Separable portions (row #61; routers/delivery.py)
export function listProjectPortions(projectId: string, includeArchived = false): Promise<import("./types").PortionsSummary & { portions: import("./types").ProjectPortion[] }> {
  return getJson(`${projUrl(projectId)}/portions${includeArchived ? "?include_archived=true" : ""}`);
}

export function createProjectPortion(projectId: string, fields: Record<string, unknown>): Promise<import("./types").ProjectPortion> {
  return postJson(`${projUrl(projectId)}/portions`, fields);
}

export function updateProjectPortion(projectId: string, portionId: number, fields: Record<string, unknown>): Promise<import("./types").ProjectPortion> {
  return patchJson(`${projUrl(projectId)}/portions/${portionId}`, fields);
}

export function reorderProjectPortions(projectId: string, ids: number[]): Promise<import("./types").ProjectPortion[]> {
  return postJson(`${projUrl(projectId)}/portions/reorder`, { ids });
}

export function archiveProjectPortion(projectId: string, portionId: number): Promise<import("./types").ProjectPortion> {
  return postJson(`${projUrl(projectId)}/portions/${portionId}/archive`, {});
}

export function restoreProjectPortion(projectId: string, portionId: number): Promise<import("./types").ProjectPortion> {
  return postJson(`${projUrl(projectId)}/portions/${portionId}/restore`, {});
}
